import { VoiceInput } from '../components/VoiceInput';
// src/modules/Settings.tsx
import React, { useState, useEffect } from 'react';
import { User } from '../types';
import { 
  Users, Shield, Clock, AlertTriangle, Mail, Database, 
  Edit, Save, Plus, Trash2, RefreshCw, Lock, CheckCircle,
  ChevronDown, ChevronRight, UserCheck, Key, Bell, FileText,
  Eye, EyeOff, UserX, UserCheck as UserCheckIcon
} from 'lucide-react';

// ─── Types ──────────────────────────────────────────────────
type RoleName =
  | 'SuperAdmin' | 'Director' | 'Nominated Individual'
  | 'Home Manager' | 'Deputy Manager'
  | 'Clinical Lead' | 'Registered Nurse' | 'Care Assistant'
  | 'Domestic Lead' | 'Kitchen Lead' | 'Housekeeping Lead' | 'Maintenance';

const ROLE_OPTIONS: RoleName[] = [
  'SuperAdmin', 'Director', 'Nominated Individual',
  'Home Manager', 'Deputy Manager',
  'Clinical Lead', 'Registered Nurse', 'Care Assistant',
  'Domestic Lead', 'Kitchen Lead', 'Housekeeping Lead', 'Maintenance',
];

interface StaffSettings {
  id: string;
  name: string;
  email: string;
  role: RoleName;
  nmcNumber?: string;
  pin?: string;
  password: string;
  active: boolean;
}

interface AuditInterval {
  id: string;
  name: string;
  frequency: 'Daily' | 'Weekly' | 'Monthly';
  activeDays: string[];
  weekOfMonth?: number;
  dayOfWeek?: string;
  escalationThreshold: number;
}

interface MissedRationale {
  id: string;
  label: string;
}

interface SeverityTier {
  id: string;
  level: 'Low' | 'Medium' | 'Critical';
  criteria: string;
}

interface CqcTrigger {
  id: string;
  category: string;
  action: string;
  requiresRef: boolean;
}

interface AlertConfig {
  directorEmails: string[];
  escalationTimerDays: number;
}

interface VaultLog {
  id: string;
  timestamp: string;
  userId: number;
  action: string;
  details: string;
}

// ─── Defaults ─────────────────────────────────────────────
const defaultStaff: StaffSettings[] = [
  { id: '1', name: 'Sarah Mitchell', email: 'admin@roopcottage.co.uk', role: 'SuperAdmin', password: 'admin123', active: true },
  { id: '2', name: 'John Doe', email: 'john@roopcottage.co.uk', role: 'Registered Nurse', password: 'password123', nmcNumber: 'NMC123456', active: true },
];

const defaultAuditIntervals: AuditInterval[] = [
  { id: '1', name: 'Daily Safety Check', frequency: 'Daily', activeDays: ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'], escalationThreshold: 85 },
  { id: '2', name: 'Weekly Clinical Review', frequency: 'Weekly', activeDays: ['Mon'], escalationThreshold: 80 },
  { id: '3', name: 'Monthly Governance', frequency: 'Monthly', activeDays: ['Mon'], weekOfMonth: 1, dayOfWeek: 'Mon', escalationThreshold: 90 },
];

const defaultRationales: MissedRationale[] = [
  { id: '1', label: 'Agency Shift Shortage' },
  { id: '2', label: 'Outbreak Control Isolation' },
  { id: '3', label: 'System Hardware Down' },
];

const defaultSeverityTiers: SeverityTier[] = [
  { id: '1', level: 'Low', criteria: 'Minor injury, no medical attention' },
  { id: '2', level: 'Medium', criteria: 'GP visit required, minor hospital treatment' },
  { id: '3', level: 'Critical', criteria: 'Unplanned hospital admission, serious injury, allegation of abuse' },
];

const defaultCqcTriggers: CqcTrigger[] = [
  { id: '1', category: 'Unplanned Hospital Admissions', action: 'Statutory Notification', requiresRef: true },
  { id: '2', category: 'Serious Injuries', action: 'Statutory Notification', requiresRef: true },
  { id: '3', category: 'Allegations of Abuse', action: 'Statutory Notification', requiresRef: true },
  { id: '4', category: 'Medication Errors', action: 'Internal Review', requiresRef: false },
];

const defaultAlertConfig: AlertConfig = {
  directorEmails: ['directors@carehome.co.uk', 'procurement@carehome.co.uk'],
  escalationTimerDays: 30,
};

// ─── Component ──────────────────────────────────────────────
export function SettingsModule({ user }: { user: User }) {
  // ─── State ──────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<'users' | 'audit' | 'incidents' | 'alerts' | 'integrity'>('users');
  const [staff, setStaff] = useState<StaffSettings[]>([]);
  const [auditIntervals, setAuditIntervals] = useState<AuditInterval[]>([]);
  const [rationales, setRationales] = useState<MissedRationale[]>([]);
  const [severities, setSeverities] = useState<SeverityTier[]>([]);
  const [cqcTriggers, setCqcTriggers] = useState<CqcTrigger[]>([]);
  const [alertConfig, setAlertConfig] = useState<AlertConfig>({ directorEmails: [], escalationTimerDays: 30 });
  const [vaultLogs, setVaultLogs] = useState<VaultLog[]>([]);
  const [message, setMessage] = useState('');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | 'all'>('all');
  const [newStaff, setNewStaff] = useState<{ name: string; email: string; role: RoleName; password: string; nmcNumber: string }>({ name: '', email: '', role: 'Care Assistant', password: '', nmcNumber: '' });

  // ─── Load data ──────────────────────────────────────────
  useEffect(() => {
    const storedStaff = localStorage.getItem('rmacs_settings_staff');
    if (storedStaff) { try { setStaff(JSON.parse(storedStaff)); } catch { setStaff(defaultStaff); } } else { setStaff(defaultStaff); localStorage.setItem('rmacs_settings_staff', JSON.stringify(defaultStaff)); }

    const storedIntervals = localStorage.getItem('rmacs_settings_intervals');
    if (storedIntervals) { try { setAuditIntervals(JSON.parse(storedIntervals)); } catch { setAuditIntervals(defaultAuditIntervals); } } else { setAuditIntervals(defaultAuditIntervals); localStorage.setItem('rmacs_settings_intervals', JSON.stringify(defaultAuditIntervals)); }

    const storedRationales = localStorage.getItem('rmacs_settings_rationales');
    if (storedRationales) { try { setRationales(JSON.parse(storedRationales)); } catch { setRationales(defaultRationales); } } else { setRationales(defaultRationales); localStorage.setItem('rmacs_settings_rationales', JSON.stringify(defaultRationales)); }

    const storedSeverities = localStorage.getItem('rmacs_settings_severities');
    if (storedSeverities) { try { setSeverities(JSON.parse(storedSeverities)); } catch { setSeverities(defaultSeverityTiers); } } else { setSeverities(defaultSeverityTiers); localStorage.setItem('rmacs_settings_severities', JSON.stringify(defaultSeverityTiers)); }

    const storedTriggers = localStorage.getItem('rmacs_settings_triggers');
    if (storedTriggers) { try { setCqcTriggers(JSON.parse(storedTriggers)); } catch { setCqcTriggers(defaultCqcTriggers); } } else { setCqcTriggers(defaultCqcTriggers); localStorage.setItem('rmacs_settings_triggers', JSON.stringify(defaultCqcTriggers)); }

    const storedAlert = localStorage.getItem('rmacs_settings_alert');
    if (storedAlert) { try { setAlertConfig(JSON.parse(storedAlert)); } catch { setAlertConfig(defaultAlertConfig); } } else { setAlertConfig(defaultAlertConfig); localStorage.setItem('rmacs_settings_alert', JSON.stringify(defaultAlertConfig)); }

    const storedLogs = localStorage.getItem('rmacs_settings_vault');
    if (storedLogs) { try { setVaultLogs(JSON.parse(storedLogs)); } catch { setVaultLogs([]); } } else {
      const sampleLogs: VaultLog[] = [
        { id: '1', timestamp: new Date().toISOString(), userId: 1, action: 'System Initialised', details: 'Settings module loaded with defaults.' },
      ];
      setVaultLogs(sampleLogs);
      localStorage.setItem('rmacs_settings_vault', JSON.stringify(sampleLogs));
    }
  }, []);

  // ─── Persistence helpers ──────────────────────────────
  const saveStaff = (data: StaffSettings[]) => { localStorage.setItem('rmacs_settings_staff', JSON.stringify(data)); setStaff(data); };
  const saveIntervals = (data: AuditInterval[]) => { localStorage.setItem('rmacs_settings_intervals', JSON.stringify(data)); setAuditIntervals(data); };
  const saveRationales = (data: MissedRationale[]) => { localStorage.setItem('rmacs_settings_rationales', JSON.stringify(data)); setRationales(data); };
  const saveSeverities = (data: SeverityTier[]) => { localStorage.setItem('rmacs_settings_severities', JSON.stringify(data)); setSeverities(data); };
  const saveTriggers = (data: CqcTrigger[]) => { localStorage.setItem('rmacs_settings_triggers', JSON.stringify(data)); setCqcTriggers(data); };
  const saveAlert = (data: AlertConfig) => { localStorage.setItem('rmacs_settings_alert', JSON.stringify(data)); setAlertConfig(data); };
  const saveVault = (data: VaultLog[]) => { localStorage.setItem('rmacs_settings_vault', JSON.stringify(data)); setVaultLogs(data); };

  // ─── Add vault log ──────────────────────────────────────
  const addVaultLog = (action: string, details: string) => {
    const newLog: VaultLog = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      userId: user.id,
      action,
      details,
    };
    saveVault([newLog, ...vaultLogs]);
  };

  // ─── Staff CRUD (enhanced) ─────────────────────────────
  const updateStaff = (id: string, updates: Partial<StaffSettings>) => {
    const updated = staff.map(s => s.id === id ? { ...s, ...updates } : s);
    saveStaff(updated);
    addVaultLog('Staff Updated', `${updates.name || ''} (${updates.role || ''})`);
    setMessage('Staff updated.');
    setTimeout(() => setMessage(''), 2000);
  };

  const deactivateStaff = (id: string) => {
    if (window.confirm('Deactivate this user? They will not be able to log in.')) {
      const updated = staff.map(s => s.id === id ? { ...s, active: false } : s);
      saveStaff(updated);
      addVaultLog('Staff Deactivated', `ID ${id}`);
      setMessage('Staff deactivated.');
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const reactivateStaff = (id: string) => {
    const updated = staff.map(s => s.id === id ? { ...s, active: true } : s);
    saveStaff(updated);
    addVaultLog('Staff Reactivated', `ID ${id}`);
    setMessage('Staff reactivated.');
    setTimeout(() => setMessage(''), 2000);
  };

  const deleteStaff = (id: string) => {
    if (window.confirm('Permanently delete this user? This cannot be undone.')) {
      const updated = staff.filter(s => s.id !== id);
      saveStaff(updated);
      addVaultLog('Staff Deleted', `ID ${id}`);
      setMessage('Staff deleted.');
      setTimeout(() => setMessage(''), 2000);
    }
  };

  const addStaffFromForm = () => {
    if (!newStaff.name.trim() || !newStaff.email.trim() || !newStaff.password) {
      setMessage('Name, email and password are all required.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    if (staff.some(s => s.email.toLowerCase() === newStaff.email.trim().toLowerCase())) {
      setMessage('A user with that email already exists.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const rec: StaffSettings = {
      id: Date.now().toString(),
      name: newStaff.name.trim(),
      email: newStaff.email.trim(),
      role: newStaff.role,
      password: newStaff.password,
      active: true,
      nmcNumber: newStaff.nmcNumber.trim() || undefined,
    };
    saveStaff([...staff, rec]);
    addVaultLog('Staff Added', `${rec.name} (${rec.role})`);
    setNewStaff({ name: '', email: '', role: 'Care Assistant', password: '', nmcNumber: '' });
    setMessage(`Staff added: ${rec.name}. Now click "Sync to Login" so they can sign in.`);
    setTimeout(() => setMessage(''), 5000);
  };

  // ─── Sync to database ───────────────────────────────────
  const syncToDatabase = async () => {
    const staffData = JSON.parse(localStorage.getItem('rmacs_settings_staff') || '[]');
    if (staffData.length === 0) { setMessage('No staff to sync.'); return; }
    setMessage('Syncing...');
    try {
      const res = await fetch('https://roopcottage.co.uk/api/index.php?action=sync_staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ staff: staffData }),
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`Synced ${data.synced} staff. They can now log in with their email and password.`);
      } else {
        setMessage('Sync failed: ' + (data.error || 'the sync_staff action may be missing from api/index.php - tell me and I will send the PHP to add it.'));
      }
    } catch (e: any) {
      setMessage('Error: ' + e.message);
    }
    setTimeout(() => setMessage(''), 5000);
  };

  // ─── Render Users tab ───────────────────────────────────
  const renderUsers = () => {
    const filtered = filterActive === 'all' ? staff : staff.filter(s => s.active === filterActive);
    const sorted = [...filtered].sort((a, b) => (a.active === b.active ? 0 : a.active ? -1 : 1));

    return (
      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <div className="flex justify-between items-center flex-wrap gap-2 mb-4">
            <h3 className="text-lg font-bold text-[#123B36]">Staff & Role Management</h3>
            <button onClick={syncToDatabase} className="bg-[#1E7F5C] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> Sync to Login
            </button>
          </div>

          <div className="mb-4 rounded-xl border border-[#E1DCCD] bg-[#FBFAF6] p-4">
            <h4 className="mb-2 text-sm font-semibold text-[#123B36]">Add a new user (no pop-ups - fill in below)</h4>
            <div className="grid gap-2 md:grid-cols-3">
              <input value={newStaff.name} onChange={e => setNewStaff(f => ({ ...f, name: e.target.value }))} placeholder="Full name" className="rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
              <input value={newStaff.email} onChange={e => setNewStaff(f => ({ ...f, email: e.target.value }))} placeholder="Email (their login)" className="rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
              <select value={newStaff.role} onChange={e => setNewStaff(f => ({ ...f, role: e.target.value as RoleName }))} className="rounded border border-[#D8D2C0] px-2 py-1 text-sm">
                {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
              </select>
              <input type="password" value={newStaff.password} onChange={e => setNewStaff(f => ({ ...f, password: e.target.value }))} placeholder="Temporary password" className="rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
              <input value={newStaff.nmcNumber} onChange={e => setNewStaff(f => ({ ...f, nmcNumber: e.target.value }))} placeholder="NMC / PIN (optional)" className="rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
              <button onClick={addStaffFromForm} className="bg-[#123B36] text-white px-4 py-1 rounded-lg text-sm flex items-center gap-2">
                <Plus className="w-4 h-4" /> Add Staff
              </button>
            </div>
            <p className="mt-2 text-xs text-[#6B7A76]">
              What each role sees: SuperAdmin / Director / Nominated Individual = everything including invoicing and reconciliation.
              Home Manager / Deputy Manager = everything EXCEPT Residents & Invoicing, Bulk Upload and Reconciliation.
              Clinical Lead / Registered Nurse = clinical and care modules. Care Assistant = daily care modules.
              Kitchen / Housekeeping / Maintenance = their own operational modules only.
            </p>
          </div>

          <div className="flex gap-2 mb-4">
            <button onClick={() => setFilterActive('all')} className={`px-3 py-1 rounded-lg text-sm ${filterActive === 'all' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#20302C]'}`}>All</button>
            <button onClick={() => setFilterActive(true)} className={`px-3 py-1 rounded-lg text-sm ${filterActive === true ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#20302C]'}`}>Active</button>
            <button onClick={() => setFilterActive(false)} className={`px-3 py-1 rounded-lg text-sm ${filterActive === false ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#20302C]'}`}>Inactive</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F5EE]">
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Email</th>
                  <th className="px-4 py-2 text-left">Role</th>
                  <th className="px-4 py-2 text-left">NMC / PIN</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(s => {
                  const isEditing = editingUserId === s.id;
                  return (
                    <tr key={s.id} className={`border-t border-[#EDE8DB] ${!s.active ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-2">
                        {isEditing ? (
                          <VoiceInput type="text" value={s.name} onChange={(e) => updateStaff(s.id, { name: e.target.value })} className="w-full rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
                        ) : s.name}
                      </td>
                      <td className="px-4 py-2">
                        {isEditing ? (
                          <input type="email" value={s.email} onChange={(e) => updateStaff(s.id, { email: e.target.value })} className="w-full rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
                        ) : s.email}
                      </td>
                      <td className="px-4 py-2">
                        {isEditing ? (
                          <select value={s.role} onChange={(e) => updateStaff(s.id, { role: e.target.value as any })} className="w-full rounded border border-[#D8D2C0] px-2 py-1 text-sm">
                            {ROLE_OPTIONS.map(r => <option key={r}>{r}</option>)}
                          </select>
                        ) : s.role}
                      </td>
                      <td className="px-4 py-2">
                        {isEditing ? (
                          <VoiceInput type="text" value={s.nmcNumber || ''} onChange={(e) => updateStaff(s.id, { nmcNumber: e.target.value || undefined })} className="w-full rounded border border-[#D8D2C0] px-2 py-1 text-sm" />
                        ) : s.nmcNumber || '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${s.active ? 'bg-[#E4F3EC] text-[#1E7F5C]' : 'bg-[#FBE4E4] text-[#C22E2E]'}`}>
                          {s.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 flex flex-wrap gap-1">
                        {isEditing ? (
                          <button onClick={() => setEditingUserId(null)} className="text-[#123B36] text-xs hover:underline">Done</button>
                        ) : (
                          <>
                            <button onClick={() => setEditingUserId(s.id)} className="text-[#123B36] text-xs hover:underline">Edit</button>
                            {s.active ? (
                              <button onClick={() => deactivateStaff(s.id)} className="text-[#C22E2E] text-xs hover:underline">Deactivate</button>
                            ) : (
                              <button onClick={() => reactivateStaff(s.id)} className="text-green-600 text-xs hover:underline">Reactivate</button>
                            )}
                            <button onClick={() => deleteStaff(s.id)} className="text-[#C22E2E] text-xs hover:underline">Delete</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-[#6B7A76] mt-2">After adding or editing staff, click "Sync to Login" to push them to the database so they can sign in.</p>
        </div>
      </div>
    );
  };

  // ─── Main render ─────────────────────────────────────────
  return (
    <div className="space-y-6">
      {message && (
        <div className="p-3 rounded-xl text-sm bg-[#E4F3EC] text-[#1E7F5C]">{message}</div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36]">System Settings</h2>
          <p className="text-[#6B7A76]">Core control panel for CQC compliance, roles, and automation</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('users')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'users' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Users</button>
          <button onClick={() => setActiveTab('audit')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'audit' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Audit Engine</button>
          <button onClick={() => setActiveTab('incidents')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'incidents' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Incidents</button>
          <button onClick={() => setActiveTab('alerts')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'alerts' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Alerts</button>
          <button onClick={() => setActiveTab('integrity')} className={`px-4 py-2 rounded-lg text-sm font-semibold ${activeTab === 'integrity' ? 'bg-[#123B36] text-white' : 'bg-[#F7F5EE] text-[#123B36]'}`}>Integrity</button>
        </div>
      </div>

      {activeTab === 'users' && renderUsers()}

      {/* Audit Engine Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
            <h3 className="text-lg font-bold text-[#123B36] mb-4">Audit Interval Engine</h3>
            {auditIntervals.map(i => (
              <div key={i.id} className="border-b border-[#EDE8DB] py-3 flex flex-wrap justify-between items-center">
                <div>
                  <p className="font-medium">{i.name}</p>
                  <p className="text-sm text-[#6B7A76]">{i.frequency} – Days: {i.activeDays.join(', ')}</p>
                  <p className="text-sm">Threshold: {i.escalationThreshold}%</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => {
                    const newThreshold = prompt('New escalation threshold (%)', String(i.escalationThreshold));
                    if (newThreshold) {
                      const updated = auditIntervals.map(a => a.id === i.id ? { ...a, escalationThreshold: parseInt(newThreshold) } : a);
                      saveIntervals(updated);
                      addVaultLog('Audit Interval Updated', i.name);
                      setMessage('Threshold updated.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#123B36] text-xs hover:underline">Edit</button>
                  <button onClick={() => {
                    if (window.confirm('Delete this interval?')) {
                      const updated = auditIntervals.filter(a => a.id !== i.id);
                      saveIntervals(updated);
                      addVaultLog('Audit Interval Deleted', i.name);
                      setMessage('Interval deleted.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#C22E2E] text-xs hover:underline">Delete</button>
                </div>
              </div>
            ))}
            <button onClick={() => {
              const name = prompt('Interval name:');
              if (!name) return;
              const freq = prompt('Frequency (Daily, Weekly, Monthly):', 'Daily');
              if (!freq) return;
              const days = prompt('Active days (comma separated, e.g. Mon,Tue,Wed):', 'Mon,Tue,Wed,Thu,Fri');
              if (!days) return;
              const thresh = prompt('Escalation threshold (%)', '85');
              if (!thresh) return;
              const newInterval: AuditInterval = {
                id: Date.now().toString(),
                name,
                frequency: freq as any,
                activeDays: days.split(',').map(d => d.trim()),
                escalationThreshold: parseInt(thresh),
              };
              saveIntervals([...auditIntervals, newInterval]);
              addVaultLog('Audit Interval Added', name);
              setMessage('Interval added.');
              setTimeout(() => setMessage(''), 2000);
            }} className="mt-4 bg-[#123B36] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Interval
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
            <h3 className="text-lg font-bold text-[#123B36] mb-4">Missed Audit Rationale</h3>
            <div className="flex flex-wrap gap-2 mb-2">
              {rationales.map(r => (
                <span key={r.id} className="bg-[#F7F5EE] px-3 py-1 rounded-full text-sm flex items-center gap-2">
                  {r.label}
                  <button onClick={() => {
                    if (window.confirm('Delete this rationale?')) {
                      const updated = rationales.filter(x => x.id !== r.id);
                      saveRationales(updated);
                      addVaultLog('Rationale Deleted', r.label);
                      setMessage('Rationale deleted.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#C22E2E] text-xs hover:underline">×</button>
                </span>
              ))}
            </div>
            <button onClick={() => {
              const label = prompt('New rationale label:');
              if (label) {
                const newR = { id: Date.now().toString(), label };
                saveRationales([...rationales, newR]);
                addVaultLog('Rationale Added', r_label_safe(label));
                setMessage('Rationale added.');
                setTimeout(() => setMessage(''), 2000);
              }
            }} className="bg-[#123B36] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Rationale
            </button>
          </div>
        </div>
      )}

      {/* Incidents Tab */}
      {activeTab === 'incidents' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
            <h3 className="text-lg font-bold text-[#123B36] mb-4">Severity Tiers</h3>
            {severities.map(s => (
              <div key={s.id} className="border-b border-[#EDE8DB] py-2 flex justify-between items-center">
                <div>
                  <span className={`font-semibold ${s.level === 'Critical' ? 'text-red-600' : s.level === 'Medium' ? 'text-yellow-600' : 'text-blue-600'}`}>{s.level}</span>
                  <span className="text-sm text-[#6B7A76] ml-2">{s.criteria}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => {
                    const newCriteria = prompt('New criteria:', s.criteria);
                    if (newCriteria) {
                      const updated = severities.map(x => x.id === s.id ? { ...x, criteria: newCriteria } : x);
                      saveSeverities(updated);
                      addVaultLog('Severity Updated', s.level);
                      setMessage('Severity updated.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#123B36] text-xs hover:underline">Edit</button>
                  <button onClick={() => {
                    if (window.confirm('Delete this severity?')) {
                      const updated = severities.filter(x => x.id !== s.id);
                      saveSeverities(updated);
                      addVaultLog('Severity Deleted', s.level);
                      setMessage('Severity deleted.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#C22E2E] text-xs hover:underline">Delete</button>
                </div>
              </div>
            ))}
            <button onClick={() => {
              const level = prompt('Level (Low, Medium, Critical):', 'Low');
              if (!level) return;
              const criteria = prompt('Criteria:', '');
              if (!criteria) return;
              const newSeverity: SeverityTier = { id: Date.now().toString(), level: level as any, criteria };
              saveSeverities([...severities, newSeverity]);
              addVaultLog('Severity Added', level);
              setMessage('Severity added.');
              setTimeout(() => setMessage(''), 2000);
            }} className="mt-4 bg-[#123B36] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Severity
            </button>
          </div>

          <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
            <h3 className="text-lg font-bold text-[#123B36] mb-4">CQC Statutory Notification Triggers</h3>
            {cqcTriggers.map(t => (
              <div key={t.id} className="border-b border-[#EDE8DB] py-2 flex justify-between items-center">
                <div>
                  <p className="font-medium">{t.category}</p>
                  <p className="text-sm text-[#6B7A76]">Action: {t.action} {t.requiresRef && '(Requires CQC Ref)'}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => {
                    const newAction = prompt('Action (Statutory Notification / Internal Review):', t.action);
                    if (newAction) {
                      const updated = cqcTriggers.map(x => x.id === t.id ? { ...x, action: newAction } : x);
                      saveTriggers(updated);
                      addVaultLog('CQC Trigger Updated', t.category);
                      setMessage('Trigger updated.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#123B36] text-xs hover:underline">Edit</button>
                  <button onClick={() => {
                    if (window.confirm('Delete this trigger?')) {
                      const updated = cqcTriggers.filter(x => x.id !== t.id);
                      saveTriggers(updated);
                      addVaultLog('CQC Trigger Deleted', t.category);
                      setMessage('Trigger deleted.');
                      setTimeout(() => setMessage(''), 2000);
                    }
                  }} className="text-[#C22E2E] text-xs hover:underline">Delete</button>
                </div>
              </div>
            ))}
            <button onClick={() => {
              const cat = prompt('Category:', '');
              if (!cat) return;
              const action = prompt('Action (Statutory Notification / Internal Review):', 'Statutory Notification');
              if (!action) return;
              const requiresRef = window.confirm('Requires CQC reference number?');
              const newTrigger: CqcTrigger = { id: Date.now().toString(), category: cat, action, requiresRef };
              saveTriggers([...cqcTriggers, newTrigger]);
              addVaultLog('CQC Trigger Added', cat);
              setMessage('Trigger added.');
              setTimeout(() => setMessage(''), 2000);
            }} className="mt-4 bg-[#123B36] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Trigger
            </button>
          </div>
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Global Automation & Alerts</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#20302C]">Director Notification Emails</label>
              <div className="flex flex-wrap gap-2 mt-1">
                {alertConfig.directorEmails.map((email, idx) => (
                  <span key={idx} className="bg-[#F7F5EE] px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {email}
                    <button onClick={() => {
                      const updated = alertConfig.directorEmails.filter((_, i) => i !== idx);
                      saveAlert({ ...alertConfig, directorEmails: updated });
                      addVaultLog('Alert Config Updated', 'Removed email');
                      setMessage('Email removed.');
                      setTimeout(() => setMessage(''), 2000);
                    }} className="text-[#C22E2E] text-xs hover:underline">×</button>
                  </span>
                ))}
              </div>
              <button onClick={() => {
                const email = prompt('Enter director email:');
                if (email) {
                  saveAlert({ ...alertConfig, directorEmails: [...alertConfig.directorEmails, email] });
                  addVaultLog('Alert Config Updated', 'Added email');
                  setMessage('Email added.');
                  setTimeout(() => setMessage(''), 2000);
                }
              }} className="mt-2 text-sm text-[#123B36] underline">+ Add Email</button>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#20302C]">DoLS Escalation Timer (Days before expiry)</label>
              <input type="number" value={alertConfig.escalationTimerDays} onChange={(e) => {
                const val = parseInt(e.target.value) || 30;
                saveAlert({ ...alertConfig, escalationTimerDays: val });
                addVaultLog('Alert Config Updated', 'Timer changed');
                setMessage('Timer updated.');
                setTimeout(() => setMessage(''), 2000);
              }} className="rounded-lg border border-[#D8D2C0] px-3 py-2 text-sm w-32" />
              <p className="text-xs text-[#6B7A76] mt-1">When an active DoLS standard application is within this many days of expiring, a daily warning flag appears.</p>
            </div>
          </div>
        </div>
      )}

      {/* Integrity Tab */}
      {activeTab === 'integrity' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
            <h3 className="text-lg font-bold text-[#123B36] mb-4">Immutable Vault Logs</h3>
            <p className="text-sm text-[#6B7A76] mb-2">Read‑only audit trail of every system state change.</p>
            <div className="max-h-60 overflow-y-auto border border-[#EDE8DB] rounded-lg">
              {vaultLogs.map(log => (
                <div key={log.id} className="border-b border-[#EDE8DB] px-4 py-2 text-sm flex justify-between">
                  <span>{new Date(log.timestamp).toLocaleString()}</span>
                  <span className="font-medium">User {log.userId}</span>
                  <span>{log.action}</span>
                  <span className="text-[#6B7A76]">{log.details}</span>
                </div>
              ))}
            </div>
            <button onClick={() => {
              if (window.confirm('Clear all logs? This action is irreversible.')) {
                saveVault([]);
                setMessage('Logs cleared.');
                setTimeout(() => setMessage(''), 2000);
              }
            }} className="mt-4 text-[#C22E2E] text-sm hover:underline">Clear Logs</button>
          </div>

          <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
            <h3 className="text-lg font-bold text-[#123B36] mb-4">Local Storage Sync Config</h3>
            <p className="text-sm text-[#6B7A76]">All settings are currently stored in browser localStorage. In production, these would sync with a central database.</p>
            <button onClick={() => {
              if (window.confirm('Reset all settings to defaults?')) {
                localStorage.removeItem('rmacs_settings_staff');
                localStorage.removeItem('rmacs_settings_intervals');
                localStorage.removeItem('rmacs_settings_rationales');
                localStorage.removeItem('rmacs_settings_severities');
                localStorage.removeItem('rmacs_settings_triggers');
                localStorage.removeItem('rmacs_settings_alert');
                localStorage.removeItem('rmacs_settings_vault');
                window.location.reload();
              }
            }} className="mt-4 text-[#C22E2E] text-sm hover:underline">Reset All Settings to Defaults</button>
          </div>
        </div>
      )}
    </div>
  );
}

function r_label_safe(label: string): string {
  return label;
}